import { ListCard, OverlappedLogos, TextLineOrSkeleton, Tooltip } from '@capsicum/common-web';
import { ChevronRightIcon, ChilizIcon } from '@capsicum/common-web/icons';
import { CurrencyType } from '@capsicum/features/wallet/shared';
import styled, { useTheme } from 'styled-components';

export type CurrencyActionCardProps = {
	currency: CurrencyType;
	disabledCHZ?: boolean;
	isLoading?: boolean;
	logos?: Array<{ enabled?: boolean; partnerName?: string; url?: string }>;
	onClick: () => void;
	showStaticLogos?: boolean;
	testID: string;
	texts: {
		amount?: string;
		logoAmount?: string;
		logoLabel?: string;
		subTitle?: string;
		title?: string;
		tooltipText?: string;
	};
};

export const CurrencyActionCard = ({
	currency,
	disabledCHZ,
	isLoading,
	logos,
	onClick,
	showStaticLogos,
	testID,
	texts
}: CurrencyActionCardProps) => {
	const { colors } = useTheme();

	const bottomLogo =
		currency === CurrencyType.CHILIZ ? (
			<>
				<S.StyledChilizIcon $disabled={disabledCHZ} color={colors.icons.main.enabled} />
				{texts.logoLabel && (
					<div>
						<S.LogoText>{texts.logoLabel}</S.LogoText>
						<S.TooltipWrapper>
							<TextLineOrSkeleton isLoading={isLoading} lineWidth={150}>
								<S.LogoAmount>{texts.logoAmount}</S.LogoAmount>
							</TextLineOrSkeleton>
							<Tooltip contentPosition="bottom_right" zIndex={9}>
								<Tooltip.Trigger testID={`${testID}-tooltip`} />
								<Tooltip.Content>
									<Tooltip.Description>{texts.tooltipText}</Tooltip.Description>
								</Tooltip.Content>
							</Tooltip>
						</S.TooltipWrapper>
					</div>
				)}
			</>
		) : (
			<OverlappedLogos logos={logos?.slice(0, 5)} showStaticLogos={showStaticLogos} text={texts.logoLabel} />
		);

	return (
		<S.ListCardWrapper testID={testID} onClick={isLoading ? undefined : onClick}>
			<ListCard.Content>
				<ListCard.Row>
					<S.ListCardRowStart>
						<S.ListCardTitle>{texts.title}</S.ListCardTitle>
						<S.ListCardSubTitle>
							<TextLineOrSkeleton isLoading={isLoading} lineWidth={240}>
								<S.StyledText>{texts.subTitle}</S.StyledText>
								{texts.amount}
							</TextLineOrSkeleton>
						</S.ListCardSubTitle>
					</S.ListCardRowStart>
					<S.ClickIcon $isLoading={isLoading} height={24} width={24} />
				</ListCard.Row>
				<S.ListCardRow>{bottomLogo}</S.ListCardRow>
			</ListCard.Content>
		</S.ListCardWrapper>
	);
};
namespace S {
	export const ClickIcon = styled(ChevronRightIcon)<{
		$isLoading?: boolean;
	}>`
		color: ${({ theme }) => theme.colors.icons.main.enabled};
		visibility: ${({ $isLoading }) => ($isLoading ? 'hidden' : 'visible')};
	`;
	export const ListCardWrapper = styled(ListCard)`
		width: 100%;
		height: auto;
		padding: ${({ theme }) => `0 ${theme.spacings.medium}`};
		cursor: pointer;
	`;
	export const ListCardRowStart = styled(ListCard.Row)`
		flex-direction: column;
		align-items: flex-start;
	`;
	export const ListCardTitle = styled(ListCard.Title)`
		flex: 1;
	`;
	export const ListCardSubTitle = styled(ListCard.SubTitle)`
		flex: 1;
		${({ theme }) => theme.fonts.text.text4}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;
	export const ListCardRow = styled(ListCard.Row)`
		justify-content: flex-start;
		gap: ${({ theme }) => theme.spacings.small};
	`;
	export const StyledText = styled.span`
		${({ theme }) => theme.fonts.text.text5}
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;
	export const LogoText = styled.div`
		${({ theme }) => theme.fonts.text.text5}
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;
	export const TooltipWrapper = styled.div`
		display: flex;
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;
	export const LogoAmount = styled.span`
		${({ theme }) => theme.fonts.text.text4}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;
	export const StyledChilizIcon = styled(ChilizIcon)<{ $disabled?: boolean }>`
		opacity: ${({ $disabled }) => ($disabled ? '0.5' : '1')};
	`;
}
